import type { PushConfigDto } from '@fas/types';
import { api } from './api';
import { useAuthStore } from './auth-store';
import { isNativeApp } from './native';

/*
 * Push notifications on this device (§36 push channel). In a browser, Web Push (Phase 2C,
 * ADR-045): the service worker (production builds) receives and shows them; the server keeps
 * one subscription per browser, bound to the sign-in session. In the Android app, Firebase Cloud
 * Messaging (Phase 3J, ADR-065): Android shows them; the server keeps the app's registration
 * token, bound the same way. Only the user who turned push on here is registered again after
 * signing in — a colleague signing in on the same device is not.
 */

export type PushState = 'unsupported' | 'unconfigured' | 'blocked' | 'off' | 'on';

const OWNER_KEY = 'fas.push.owner';
const APP_TOKEN_KEY = 'fas.push.app-token';
const REGISTRATION_TIMEOUT_MS = 20_000;

function supported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

let config: Promise<PushConfigDto> | null = null;

function pushConfig(): Promise<PushConfigDto> {
  config ??= api.get<PushConfigDto>('/notifications/push/config').catch((err: unknown) => {
    config = null;
    throw err;
  });
  return config;
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64url.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function sameKey(current: ArrayBuffer | null, publicKey: string): boolean {
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = keyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function currentUserId(): string | null {
  return useAuthStore.getState().me?.user.id ?? null;
}

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage may be blocked; push still works for this visit.
  }
}

const owner = () => stored(OWNER_KEY);
const rememberOwner = (userId: string | null) => store(OWNER_KEY, userId);

// ── The Android app: Firebase Cloud Messaging through the Capacitor plugin ────────
type AppPush = (typeof import('@capacitor/push-notifications'))['PushNotifications'];

/**
 * The plugin's module, loaded only inside the app. Take the plugin from it; never resolve a promise
 * with the plugin itself: it is a proxy that answers every property — `then` too — so a promise
 * resolved with it waits forever for that `then`.
 */
const appPushModule = () => import('@capacitor/push-notifications');

/** Android's notification permission (asked for on Android 13 and later). */
async function appPermission(push: AppPush, ask: boolean): Promise<'granted' | 'denied' | 'prompt'> {
  let { receive } = await push.checkPermissions();
  if (ask && receive !== 'granted' && receive !== 'denied') ({ receive } = await push.requestPermissions());
  return receive === 'granted' || receive === 'denied' ? receive : 'prompt';
}

/** Registers the app with Firebase and resolves its registration token. */
async function appToken(push: AppPush): Promise<string> {
  let settle!: { resolve: (token: string) => void; reject: (err: Error) => void };
  const token = new Promise<string>((resolve, reject) => {
    settle = { resolve, reject };
  });
  const listeners = await Promise.all([
    push.addListener('registration', ({ value }) => settle.resolve(value)),
    push.addListener('registrationError', () => settle.reject(new Error('This device could not be registered for notifications. Check the connection and try again.'))),
  ]);
  const timer = setTimeout(() => settle.reject(new Error('Registering this device for notifications took too long. Try again.')), REGISTRATION_TIMEOUT_MS);
  try {
    await push.register();
    return await token;
  } finally {
    clearTimeout(timer);
    await Promise.all(listeners.map((l) => l.remove()));
  }
}

async function registerApp(push: AppPush): Promise<void> {
  const token = await appToken(push);
  await api.post('/notifications/push/native', { platform: 'ANDROID', token });
  store(APP_TOKEN_KEY, token);
}

async function appState(): Promise<PushState> {
  if (!(await pushConfig()).nativeEnabled) return 'unconfigured';
  const { PushNotifications: push } = await appPushModule();
  const permission = await appPermission(push, false);
  if (permission === 'denied') return 'blocked';
  return permission === 'granted' && stored(APP_TOKEN_KEY) !== null && owner() === currentUserId() ? 'on' : 'off';
}

async function enableApp(): Promise<PushState> {
  if (!(await pushConfig()).nativeEnabled) return 'unconfigured';
  const { PushNotifications: push } = await appPushModule();
  const permission = await appPermission(push, true);
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  await registerApp(push);
  rememberOwner(currentUserId());
  return 'on';
}

async function disableApp(): Promise<PushState> {
  const token = stored(APP_TOKEN_KEY);
  if (token) await api.post('/notifications/push/native/remove', { token }).catch(() => undefined);
  const { PushNotifications: push } = await appPushModule();
  await push.unregister().catch(() => undefined);
  store(APP_TOKEN_KEY, null);
  rememberOwner(null);
  return 'off';
}

/** Firebase replaced the app's token while push is on here: the owner's new token is registered. */
export async function appPushTokenChanged(token: string): Promise<void> {
  const current = stored(APP_TOKEN_KEY);
  if (!current || current === token || owner() !== currentUserId()) return;
  try {
    await api.post('/notifications/push/native', { platform: 'ANDROID', token });
    store(APP_TOKEN_KEY, token);
  } catch {
    // Registered again at the next sign-in.
  }
}

// ── This device ───────────────────────────────────────────────────────────────
export async function pushState(): Promise<PushState> {
  if (isNativeApp()) return appState();
  const reg = await registration();
  if (!reg) return 'unsupported';
  if (!(await pushConfig()).enabled) return 'unconfigured';
  if (Notification.permission === 'denied') return 'blocked';
  const subscription = await reg.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' && owner() === currentUserId() ? 'on' : 'off';
}

/** Asks for permission, subscribes this browser (or the app) and registers it for the signed-in user. */
export async function enablePush(): Promise<PushState> {
  if (isNativeApp()) return enableApp();
  const reg = await registration();
  if (!reg) return 'unsupported';
  const cfg = await pushConfig();
  if (!cfg.enabled || !cfg.publicKey) return 'unconfigured';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  let subscription = await reg.pushManager.getSubscription();
  // A subscription made with another server key cannot receive this server's messages.
  if (subscription && !sameKey(subscription.options.applicationServerKey, cfg.publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.publicKey) });
  await api.post('/notifications/push/subscriptions', subscription.toJSON());
  rememberOwner(currentUserId());
  return 'on';
}

/** Stops push on this browser (or in the app) for everyone. */
export async function disablePush(): Promise<PushState> {
  if (isNativeApp()) return disableApp();
  const subscription = await (await registration())?.pushManager.getSubscription();
  if (subscription) {
    await api.post('/notifications/push/subscriptions/remove', { endpoint: subscription.endpoint }).catch(() => undefined);
    await subscription.unsubscribe();
  }
  rememberOwner(null);
  return 'off';
}

/** After signing in: re-registers this device for the new session — only for its owner. */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (isNativeApp()) {
      if (owner() !== currentUserId() || !(await pushConfig()).nativeEnabled) return;
      const { PushNotifications: push } = await appPushModule();
      if ((await appPermission(push, false)) === 'granted') await registerApp(push);
      return;
    }
    if (!supported() || Notification.permission !== 'granted' || owner() !== currentUserId()) return;
    const subscription = await (await registration())?.pushManager.getSubscription();
    if (!subscription || !(await pushConfig()).enabled) return;
    await api.post('/notifications/push/subscriptions', subscription.toJSON());
  } catch {
    // Best effort: the account page shows the state and can turn push on again.
  }
}

/** Before signing out: the server stops pushing this user's notifications to this device. */
export async function forgetPushSubscription(): Promise<void> {
  try {
    if (isNativeApp()) {
      const token = stored(APP_TOKEN_KEY);
      if (token) await api.post('/notifications/push/native/remove', { token });
      return;
    }
    const subscription = await (await registration())?.pushManager.getSubscription();
    if (subscription) await api.post('/notifications/push/subscriptions/remove', { endpoint: subscription.endpoint });
  } catch {
    // Signing out ends the session anyway, and the server pushes only to live sessions.
  }
}

/** Test hook. */
export function resetPushForTests(): void {
  config = null;
}
